// src/lib/format.ts
//
// Formatting only — never arithmetic. Every function here takes a value the
// API already computed and renders it; none of them add, subtract or split a
// money amount. See ENGINEERING.md's build brief: "Money is integer cents from the
// API. Format for display only; never do arithmetic on it in the browser."
import type { BillStatus } from './types'

const SGD = new Intl.NumberFormat('en-SG', {
  style: 'currency',
  currency: 'SGD',
  currencyDisplay: 'code',
})

/** Integer cents -> "SGD 1,240.00". `Intl.NumberFormat` divides for DISPLAY only. */
export function formatCents(cents: number): string {
  return SGD.format(cents / 100)
}

const DATE = new Intl.DateTimeFormat('en-SG', { year: 'numeric', month: 'short', day: '2-digit' })

/** "2026-08-10" -> "10 Aug 2026". Never shifts the calendar day — parsed as UTC. */
export function formatIsoDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) return isoDate
  return DATE.format(new Date(Date.UTC(year, month - 1, day)))
}

const INSTANT = new Intl.DateTimeFormat('en-SG', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: 'Asia/Singapore',
  hour12: false,
})

/** A canonical UTC instant string -> a Singapore-local rendering. */
export function formatInstant(instant: string): string {
  return `${INSTANT.format(new Date(instant))} SGT`
}

/** Basis points (1/100 of a percent) -> "60.00%". */
export function formatBasisPoints(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`
}

export const BILL_TYPE_LABELS: Record<string, string> = {
  NEW: 'New bill',
  UPWARD_ADJUSTMENT: 'Upward adjustment',
  DOWNWARD_ADJUSTMENT: 'Downward adjustment',
}

/** The three-letter shorthand the direction comp uses: NB / UA / DA. */
export const BILL_TYPE_SHORT: Record<string, string> = {
  NEW: 'NB',
  UPWARD_ADJUSTMENT: 'UA',
  DOWNWARD_ADJUSTMENT: 'DA',
}

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  SUBMITTED: 'Submitted',
  AWAITING_L1: 'Awaiting level 1 approval',
  AWAITING_L2: 'Awaiting level 2 approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  // ★★ WAVE 10 — the label says WHO IS BEING WAITED ON, which "Information
  // requested" alone does not. An officer scanning a queue has to be able to tell
  // this state from the two awaiting-approval ones at a glance: those are waiting
  // on a COLLEAGUE, this one is waiting on the HSP and no approval action will move
  // it until it is resumed.
  INFORMATION_REQUESTED: 'Paused: awaiting information from the HSP',
}

export function billStatusLabel(status: string): string {
  return BILL_STATUS_LABELS[status as BillStatus] ?? status
}

export function billTypeLabel(billType: string): string {
  return BILL_TYPE_LABELS[billType] ?? billType
}

const SETTLEMENT_STATE_LABELS: Readonly<Record<string, string>> = {
  NOT_SETTLED: 'Not settled yet',
  EMITTED: 'Sent for payment',
  PAID: 'Paid',
  PARTIALLY_PAID: 'Partly paid',
  PAYMENT_REJECTED: 'Payment rejected',
  OVERPAID: 'Overpaid',
}

/** A bill's settlement state in words an officer reads; an unknown value is shown as the server sent it. */
export function settlementStateLabel(state: string): string {
  return SETTLEMENT_STATE_LABELS[state] ?? state
}
